import { TransitionEvent, useEffect, useMemo, useRef, useState } from 'react';
import { SelectOption } from 'typescript/components/select_and_reveal/types';
import SelectOptionCard from 'typescript/components/select_and_reveal/select_option_card';
import {
  CARD_GAP,
  CARD_HEIGHT,
  TRANSITION_TIME,
} from 'typescript/components/select_and_reveal/shared_styles';
import styles from 'typescript/components/select_and_reveal/select_and_reveal.module.scss';

export const SELECT_AND_REVEAL_TEST_ID = 'select-and-reveal-test-id';
export const SELECT_OPTIONS_TEST_ID = 'select-options-test-id';
export const COMPONENT_TO_REVEAL_TEST_ID = 'component-to-reveal-test-id';

interface SelectAndRevealProps {
  className?: string;
  selectOptions?: SelectOption[];
  optionSelected?: number;
  setOptionSelected?: (option: number | null) => void;
  selectOptionCardDisabled?: boolean;
}

const SelectAndReveal = ({
  className,
  selectOptions,
  optionSelected = null,
  setOptionSelected,
  selectOptionCardDisabled = false,
}: SelectAndRevealProps) => {
  const selectContainerRef = useRef<HTMLDivElement>(null);
  const componentToRevealContainerRef = useRef<HTMLDivElement | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isComponentRevealed, setIsComponentRevealed] = useState(false);
  const [fullyExpanded, setFullyExpanded] = useState(false);
  const [collapsedCardIndex, setCollapsedCardIndex] = useState<number | null>(
    null,
  );
  const [
    componentToRevealContainerHeight,
    setComponentToRevealContainerHeight,
  ] = useState(0);

  const componentToReveal = useMemo(() => {
    return selectOptions?.[collapsedCardIndex]?.revealComponent;
  }, [selectOptions, collapsedCardIndex]);

  const selectContainerClassName = useMemo(() => {
    return `${styles.selectContainer} ${collapsedCardIndex === null ? '' : styles.containerCollapsed}`;
  }, [collapsedCardIndex]);

  const componentToRevealContainerClassName = useMemo(() => {
    const baseClassName = styles.componentToRevealContainer;
    const componentClassName =
      selectOptions[collapsedCardIndex]?.revealComponent?.props?.className ||
      '';
    const fullyExpandedClassName = fullyExpanded ? styles.fullyExpanded : '';
    const revealClassName = isComponentRevealed ? styles.reveal : styles.hidden;

    return `${baseClassName} ${componentClassName} ${fullyExpandedClassName} ${styles.componentToReveal} ${revealClassName}`;
  }, [selectOptions, isComponentRevealed, fullyExpanded]);

  // handle the click event on the option cards
  const handleCardClick = (index: number) => {
    if (isTransitioning || selectOptions.length === 1) {
      return;
    }

    setIsTransitioning(true);

    if (collapsedCardIndex !== null && isComponentRevealed) {
      // collapse the componentToReveal first
      setIsComponentRevealed(false);
      setFullyExpanded(false);

      // wait for the collapse animation to complete
      setTimeout(() => {
        setCollapsedCardIndex(null);
        setOptionSelected?.(null);
      }, TRANSITION_TIME);

      return;
    }

    setCollapsedCardIndex(index);
    setOptionSelected?.(index);
  };

  // handle the height transition end event to update the state of the component
  const handleTransitionEnd = (event: TransitionEvent) => {
    if (event.propertyName !== 'height') return;

    if (collapsedCardIndex !== null) {
      // first expanding the componentToReveal
      setIsComponentRevealed(true);

      // some styling needs to be applied after the component is fully expanded
      // it is set in a timeout to avoid some weird animation issues while the container is initially expanding
      setTimeout(() => {
        setFullyExpanded(true);
        setIsTransitioning(false);
      }, TRANSITION_TIME);

      return;
    }

    setIsTransitioning(false);
  };

  // each option card has a different class name based on the state of the component
  const calculateClassName = (index: number) => {
    const isFirstCard = index === 0;
    const isCurrentCard = collapsedCardIndex === index;

    // the first card has some special rules to keep the animations clean
    // keep them all in one if statement to make it a bit more clear
    if (isFirstCard) {
      if (collapsedCardIndex === null) {
        return `${styles.card} ${styles.firstCard}`;
      }

      return isCurrentCard ? styles.card : styles.firstCardHidden;
    }

    // all other card behavior
    if (collapsedCardIndex === null) return styles.card;

    return isCurrentCard
      ? `${styles.card} ${styles.collapseOver} ${styles.selectCardReveal}`
      : `${styles.card} ${styles.collapseUnder} ${styles.selectCardHidden}`;
  };

  // necessary useEffect to attach a resize observer to the componentToReveal element
  // since the height of the componentToReveal is dynamic and we want to be able to adjust
  // the height of the container accordingly
  useEffect(() => {
    const container = componentToRevealContainerRef.current;
    if (!container) return undefined;

    const componentToRevealElement = container.firstChild as HTMLElement;
    if (!componentToRevealElement) return undefined;

    if (componentToRevealElement) {
      const resizeObserver = new ResizeObserver(() => {
        setComponentToRevealContainerHeight(
          componentToRevealElement.offsetHeight,
        );
      });

      resizeObserver.observe(componentToRevealElement);

      // cleanup function to disconnect the observer when the component unmounts or node changes
      return () => {
        resizeObserver.disconnect();
      };
    }

    return undefined;
  }, [componentToReveal]);

  // when the optionSelected prop is passed in, we need to set all the state variables accordingly
  useEffect(() => {
    if (optionSelected !== null && selectOptions.length > 1) {
      handleCardClick(optionSelected);
    }
  }, [optionSelected]);

  // if there are no options, don't render anything
  if (!selectOptions || selectOptions.length === 0) {
    return null;
  }

  // if only 1 option is available, we don't need to show the select options
  if (selectOptions.length === 1) {
    return (
      <div
        data-testid={SELECT_AND_REVEAL_TEST_ID}
        className={`${className} shop-grid-container`}
      >
        <div
          data-testid={SELECT_OPTIONS_TEST_ID}
          className={`shop-grid-item-full ${selectContainerClassName}`}
          onTransitionEnd={handleTransitionEnd}
        >
          <SelectOptionCard
            index={0}
            className={styles.card}
            card={selectOptions[0]}
            displayDropdownWhenCollapsed={false}
            cardType="content"
          />
        </div>
        <div className="shop-grid-item-full">
          {selectOptions[0].revealComponent}
        </div>
      </div>
    );
  }

  // for when there are more than 1 option
  return (
    <div data-testid={SELECT_AND_REVEAL_TEST_ID} className={className}>
      <div
        data-testid={SELECT_OPTIONS_TEST_ID}
        ref={selectContainerRef}
        className={`shop-grid-item-full ${selectContainerClassName}`}
        style={{
          height: collapsedCardIndex
            ? CARD_HEIGHT
            : (CARD_HEIGHT + CARD_GAP) * selectOptions.length - CARD_GAP,
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {selectOptions.map((card, index) => (
          <SelectOptionCard
            key={`select-option-card-${index}`}
            index={index}
            className={calculateClassName(index)}
            handleCardClick={handleCardClick}
            card={card}
            displayDropdownWhenCollapsed={collapsedCardIndex === index}
            disabled={selectOptionCardDisabled}
          />
        ))}
      </div>
      <div
        data-testid={COMPONENT_TO_REVEAL_TEST_ID}
        ref={componentToRevealContainerRef}
        className={`shop-grid-item-full ${componentToRevealContainerClassName}`}
        style={{
          height: isComponentRevealed ? componentToRevealContainerHeight : 0,
        }}
      >
        {componentToReveal}
      </div>
    </div>
  );
};

export default SelectAndReveal;
